import { useCallback, useContext, useEffect, useMemo } from 'react';
import dayjs from 'dayjs';
import { uniqBy } from 'lodash';

import { type AutocompleteValue, type UseAutocompleteOptionsArgs } from './autocomplete.types';
import { type BookYearOption } from '@/types/trpc/hoa';

import { AppContext } from '@/shared/context/context';
import { BookyearStatus } from '~/common/enums';
import { localStorageKeys } from '@/shared/utils/constants';
import { trpc } from '@/config/trpc';

import useMap, { type MapReturn } from '../useMap';
import useAutocompleteOptions from './useAutocompleteOptions';
import useEvent from '../useEvent';
import useScopedLocalStorage from '../localStorage/useScopedLocalStorage';

type PersistedBookYear = {
  bookYearId: string;
  homeownerAssociationId: string;
};

type PersistenceOptions = {
  disabled?: boolean;
  homeownerAssociationId: string | null | undefined;
};

const usePersistedBookYears = (options: PersistenceOptions) => {
  const { disabled, homeownerAssociationId } = options;

  const [persistedBookYears, setPersistedBookYears] = useScopedLocalStorage<PersistedBookYear[]>(
    disabled === true ? null : localStorageKeys.bookYear.SELECTOR,
    [],
  );

  const saveForCurrentHOA = useCallback(
    (bookYearOptions: BookYearOption[] | BookYearOption | null) => {
      if (!bookYearOptions || !homeownerAssociationId) return;

      const toPersist = Array.isArray(bookYearOptions) ? bookYearOptions : [bookYearOptions];

      setPersistedBookYears((prevBookYears) => {
        const newHOABookYears = toPersist.map((option) => ({
          bookYearId: option.value,
          homeownerAssociationId,
        }));
        const prevDifHOABookYears = prevBookYears.filter(
          (bookYear) => bookYear.homeownerAssociationId !== homeownerAssociationId,
        );

        return uniqBy(
          [...prevDifHOABookYears, ...newHOABookYears],
          (bookYear) => `${bookYear.homeownerAssociationId}-${bookYear.bookYearId}`,
        );
      });
    },
    [homeownerAssociationId, setPersistedBookYears],
  );

  const persistedHOABookYearIds = useMemo(
    () =>
      persistedBookYears
        .filter((bookYear) => bookYear.homeownerAssociationId === homeownerAssociationId)
        .map((bookYear) => bookYear.bookYearId),
    [homeownerAssociationId, persistedBookYears],
  );

  const persistedData = useMemo(
    () => ({
      all: persistedBookYears,
      hoa: persistedHOABookYearIds,
    }),
    [persistedBookYears, persistedHOABookYearIds],
  );

  const helpers = useMemo(
    () => ({
      saveForCurrentHOA,
    }),
    [saveForCurrentHOA],
  );

  return [persistedData, helpers] as const;
};

const useBookYearOptionsMap = (bookYearOptions: BookYearOption[] | undefined) => {
  const entries = useMemo(
    () => bookYearOptions?.map((option) => [option.value, option] as const),
    [bookYearOptions],
  );

  return useMap(entries);
};

type InitialOptions = {
  bookYearOptionsMap: MapReturn<string, BookYearOption>;
  global?: boolean;
  homeownerAssociationId: string | null | undefined;
  persist?: boolean;
};

const useInitialBookYearOptions = (options: InitialOptions) => {
  const [persistedBookYears, helpers] = usePersistedBookYears({
    disabled: !options.persist,
    homeownerAssociationId: options.homeownerAssociationId,
  });

  const { ui } = useContext(AppContext);

  const getInitialCtxOption = useCallback(
    () =>
      options.global && ui.currentBookYearId
        ? options.bookYearOptionsMap.get(ui.currentBookYearId)
        : null,
    [options.bookYearOptionsMap, options.global, ui.currentBookYearId],
  );

  const getCurrentYearOption = useCallback(() => {
    let latestOption: BookYearOption | undefined;
    let matchingOption: BookYearOption | undefined;

    const today = dayjs();

    options.bookYearOptionsMap.entries().forEach(([_key, option]) => {
      const optionDates = {
        end: dayjs(option.data.endDate),
        start: dayjs(option.data.startDate),
      };

      if (
        !matchingOption &&
        optionDates.end.isSameOrAfter(today) &&
        optionDates.start.isSameOrBefore(today)
      )
        matchingOption = option;
      if (!latestOption || optionDates.end.isAfter(latestOption.data.endDate))
        latestOption = option;
    });

    return matchingOption ?? latestOption ?? null;
  }, [options.bookYearOptionsMap]);

  const initialMulti = useMemo(() => {
    if (persistedBookYears.hoa.length)
      return persistedBookYears.hoa
        .map(options.bookYearOptionsMap.get)
        .filter((res): res is BookYearOption => !!res);

    const fallbackOption = getInitialCtxOption() ?? getCurrentYearOption();

    return fallbackOption ? [fallbackOption] : [];
  }, [
    getCurrentYearOption,
    getInitialCtxOption,
    options.bookYearOptionsMap.get,
    persistedBookYears.hoa,
  ]);

  const [initialSingle = null] = initialMulti;

  const initialOptions = useMemo(
    () => ({
      multi: initialMulti,
      single: initialSingle,
    }),
    [initialMulti, initialSingle],
  );

  return [initialOptions, helpers] as const;
};

type AutocompleteOptions<
  TMultiple extends boolean | undefined,
  TDisableClearable extends boolean | undefined,
> = {
  activeOnly?: boolean;
  globalAsDefault?: boolean;
  homeownerAssociationId: string | null | undefined;
  persist?: boolean;
} & UseAutocompleteOptionsArgs<BookYearOption[], TMultiple, TDisableClearable>;

const useBookYearAutocomplete = <
  TMultiple extends boolean | undefined = undefined,
  TDisableClearable extends boolean | undefined = undefined,
>({
  activeOnly = false,
  callback,
  disableClearable,
  globalAsDefault = false,
  homeownerAssociationId,
  persist = false,
  multiple,
  selected,
  ...args
}: AutocompleteOptions<TMultiple, TDisableClearable>) => {
  type Value = AutocompleteValue<BookYearOption, TMultiple, TDisableClearable>;

  const { data: options, isLoading } = trpc.hoa.bookYear.autocomplete.useQuery(
    {
      bookYearStatus: activeOnly ? [BookyearStatus.Open] : undefined,
      homeownerAssociationId: homeownerAssociationId as string,
    },
    { enabled: !!homeownerAssociationId },
  );

  const optionsMap = useBookYearOptionsMap(options);

  const [initialOptions, helpers] = useInitialBookYearOptions({
    bookYearOptionsMap: optionsMap,
    global: globalAsDefault,
    homeownerAssociationId,
    persist,
  });

  const autocompleteProps = useAutocompleteOptions({
    callback: (value) => {
      if (persist) helpers.saveForCurrentHOA(value);

      callback?.(value);
    },
    data: options,
    disableClearable,
    isLoading,
    multiple,
    selected,
    ...args,
  });

  const prefillSingle = useEvent((toPrefill: BookYearOption | null) => {
    if (autocompleteProps.value || !toPrefill) return;

    callback(toPrefill as Value);
  });

  const prefillMulti = useEvent((toPrefill: BookYearOption[]) => {
    if (!Array.isArray(autocompleteProps.value) || autocompleteProps.value.length) return;

    callback(toPrefill as Value);
  });

  useEffect(
    () => (multiple ? prefillMulti(initialOptions.multi) : prefillSingle(initialOptions.single)),
    [initialOptions.multi, initialOptions.single, multiple, prefillMulti, prefillSingle],
  );

  return useMemo(
    () => ({
      ...autocompleteProps,
      disabled: !homeownerAssociationId || autocompleteProps.disabled,
    }),
    [autocompleteProps, homeownerAssociationId],
  );
};

export default useBookYearAutocomplete;
